import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Icons } from "../lib/icons";
import { Btn, Pill, FactorChip, CopyBtn } from "../lib/primitives";
import { SaveBar } from "../components/SaveBar";
import {
  apiClient,
  ApiError,
  type FactorCombination,
  type LivenessAvailability,
  type LivenessMode,
  type ProviderPolicy,
} from "../lib/api";
import { useOperatorRole } from "../lib/hooks";
import { LIVENESS_REASON_LABELS } from "../lib/liveness";

// Slider bounds match server-side validation in `server/db/provider-policy.ts`.
const AGENT_RATE_MIN = 10;
const AGENT_RATE_MAX = 120;

// Which factor combinations require continuing on the user's phone (sensor-bound
// ceremonies). AuthenSee decides handoff automatically; shown here for awareness.
const FACTOR_REQUIRES_PHONE: Record<string, boolean> = {
  passkey_and_behavior: true,
  passkey_and_image_points: false,
  passkey_only: false,
};

// V1 policy is a menu of pre-defined factor combinations (no custom Boolean
// logic). Ids match the `factorCombination` enum validated server-side in
// `server/db/provider-policy.ts`.
type PolicyOption = {
  id: ProviderPolicy["factorCombination"];
  name: string;
  sub: string;
  factors: string[];
  recommended: boolean;
  coverage: string;
};

const POLICY_OPTIONS: PolicyOption[] = [
  {
    id: "passkey_and_image_points",
    name: "Passkey and picture spots",
    sub: "Face ID, a fingerprint or a security key, then two spots on a picture that only the person knows. The spots never leave their device: we check a zero-knowledge proof.",
    factors: ["passkey", "image_points"],
    recommended: false,
    coverage: "Two factors · AAL2 equivalent · phishing-resistant",
  },
  {
    id: "passkey_and_behavior",
    name: "Passkey and motion",
    sub: "Face ID, a fingerprint or a security key, then a short motion made with the phone. The motion stays on the phone: we check a zero-knowledge proof.",
    factors: ["passkey", "behavior_motion"],
    recommended: false,
    coverage: "Two factors · AAL2 equivalent · phishing-resistant",
  },
  {
    id: "passkey_only",
    name: "Passkey only",
    sub: "Face ID, a fingerprint or a security key. Fine for lower-risk apps.",
    factors: ["passkey"],
    recommended: false,
    coverage: "One factor · AAL1 equivalent · phishing-resistant",
  },
];

function PolicyCard({ option, active, onClick }: { option: any; active: boolean; onClick: () => void }) {
  return (
    <div className="ad-radio-card" data-checked={active} onClick={onClick}>
      <div className="ad-radio-card-head">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: "var(--ad-fg-1)" }}>{option.name}</div>
          {option.recommended && <Pill kind="orange">Recommended</Pill>}
        </div>
        <div className="ad-radio-card-dot" />
      </div>
      <div style={{ fontSize: 14, lineHeight: 1.5, color: "var(--ad-fg-2)", marginBottom: 12 }}>{option.sub}</div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
        {option.factors.map((f: string) => <FactorChip key={f} name={f} />)}
      </div>
      <div style={{ fontSize: 13, color: "var(--ad-fg-3)" }}>{option.coverage}</div>
      <div style={{ fontSize: 12, color: "var(--ad-fg-4)", fontFamily: "var(--rb-font-mono)", marginTop: 4 }}>{option.id}</div>
    </div>
  );
}

function Banner({ icon, title, sub }: { icon: any; title: string; sub: string }) {
  return (
    <div className="ad-banner">
      <div className="ad-banner-icon">{icon}</div>
      <div className="ad-banner-body">
        <div className="ad-banner-title">{title}</div>
        <div className="ad-banner-sub">{sub}</div>
      </div>
    </div>
  );
}

// Liveness auth gate — a policy-orthogonal add-on. Monitor is publishable
// with any policy; enforce additionally needs a ceremony that collects the
// attestation, and both are capped per deployment (staging monitor, prod off).
const LIVENESS_OPTIONS: { id: LivenessMode; name: string; sub: string }[] = [
  {
    id: "off",
    name: "Off",
    sub: "No liveness check.",
  },
  {
    id: "monitor",
    name: "Monitor",
    sub: "Run the check and record the result, but let everyone through. Try this before you enforce.",
  },
  {
    id: "enforce",
    name: "Enforce",
    sub: "People must pass the check on their phone, or the sign-in is refused.",
  },
];

const LIVENESS_RANK: Record<LivenessMode, number> = { off: 0, monitor: 1, enforce: 2 };

/** Why a mode cannot be published right now, or null if it can. */
function livenessBlockReason(
  mode: LivenessMode,
  availability: LivenessAvailability | undefined,
  factorCombination: FactorCombination,
): string | null {
  if (mode === "off" || !availability) return null;
  if (LIVENESS_RANK[mode] > LIVENESS_RANK[availability.maxMode]) {
    return "Not available here yet.";
  }
  if (mode === "enforce" && !availability.enforceReadyCombinations.includes(factorCombination)) {
    return "Not available with this sign-in method yet.";
  }
  return null;
}

function LivenessGateCard({
  mode,
  onSelect,
  canWrite,
  isLoading,
  pending,
  availability,
  factorCombination,
  stats,
}: {
  mode: LivenessMode;
  onSelect: (m: LivenessMode) => void;
  canWrite: boolean;
  isLoading: boolean;
  pending: boolean;
  availability: LivenessAvailability | undefined;
  factorCombination: FactorCombination;
  stats: import("../lib/api").LivenessStats | undefined;
}) {
  const select = (next: LivenessMode) => {
    if (!canWrite || pending || next === mode) return;
    if (livenessBlockReason(next, availability, factorCombination)) return;
    onSelect(next);
  };

  return (
    <div className="ad-card">
      <div className="ad-card-head">
        <div>
          <div className="ad-card-title">Liveness</div>
          <div style={{ fontSize: 14, color: "var(--ad-fg-3)", marginTop: 4 }}>
            A quick camera check that a real person is there, on top of the
            method above.
          </div>
        </div>
        {canWrite ? (
          <Pill kind={mode === "off" ? "muted" : "orange"}>{LIVENESS_OPTIONS.find((o) => o.id === mode)?.name ?? mode}</Pill>
        ) : (
          <Pill kind="muted">Read-only</Pill>
        )}
      </div>
      <div style={{ fontSize: 13, color: "var(--ad-fg-3)", marginBottom: 12 }}>
        Last 24 hours: {stats?.trials ? `${stats.trials} checks, ${stats.passed} passed, ${stats.failed} failed${stats.topFailureReason ? `. Most common failure: ${LIVENESS_REASON_LABELS[stats.topFailureReason] ?? stats.topFailureReason}` : ""}` : "no checks"}
      </div>

      {isLoading ? (
        <div style={{ fontSize: 13, color: "var(--ad-fg-3)" }}>Loading…</div>
      ) : (
        <div className="ad-grid-2">
          {LIVENESS_OPTIONS.map((opt) => {
            const blockReason = livenessBlockReason(opt.id, availability, factorCombination);
            const disabled = !canWrite || pending || blockReason !== null;
            return (
              <div
                key={opt.id}
                className="ad-radio-card"
                data-checked={mode === opt.id}
                onClick={() => select(opt.id)}
                aria-disabled={disabled}
                style={disabled ? ({ cursor: "default", opacity: opt.id === "off" ? 1 : 0.55 } as React.CSSProperties) : undefined}
              >
                <div className="ad-radio-card-head">
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {opt.id === "enforce" ? (
                      <Icons.Lock size={16} style={{ color: "var(--ad-orange-ink)" } as React.CSSProperties} />
                    ) : opt.id === "monitor" ? (
                      <Icons.Fingerprint size={16} style={{ color: "var(--ad-fg-2)" } as React.CSSProperties} />
                    ) : (
                      <Icons.Shield size={16} style={{ color: "var(--ad-fg-3)" } as React.CSSProperties} />
                    )}
                    <div style={{ fontSize: 15, fontWeight: 600 }}>{opt.name}</div>
                  </div>
                  <div className="ad-radio-card-dot" />
                </div>
                <div style={{ fontSize: 14, color: "var(--ad-fg-2)" }}>{opt.sub}</div>
                {blockReason && (
                  <div style={{ fontSize: 13, color: "var(--ad-fg-3)", marginTop: 8 }}>
                    {blockReason}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {mode === "enforce" && (
        <div style={{ marginTop: 12 }}>
          <Banner
            icon={<Icons.Fingerprint />}
            title="Continues on their phone"
            sub="The check needs a camera, so people finish it on their phone. A sign-in without a passed check is refused."
          />
        </div>
      )}
    </div>
  );
}

export function Policy() {
  const queryClient = useQueryClient();
  const { canWrite } = useOperatorRole();
  const { data, isLoading } = useQuery({
    queryKey: ["policy"],
    queryFn: () => apiClient.policy.get(),
    staleTime: 60_000,
  });
  // Liveness gate lives on its own resource but shares this screen's
  // draft/publish flow, so its state is managed here (not in the card).
  const { data: gates, isLoading: gatesLoading } = useQuery({
    queryKey: ["auth-gates"],
    queryFn: () => apiClient.authGates.get(),
    staleTime: 60_000,
  });
  const { data: analytics } = useQuery({ queryKey: ["analytics"], queryFn: apiClient.dashboard.analytics });

  // Local draft state mirrors `data` until the operator publishes. We keep
  // the "Discard" affordance by recomputing the draft from `data` on reset.
  const [draft, setDraft] = useState<ProviderPolicy | null>(null);
  const [livenessDraft, setLivenessDraft] = useState<LivenessMode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    if (data && !draft) setDraft(data);
  }, [data, draft]);
  useEffect(() => {
    if (gates && livenessDraft === null) setLivenessDraft(gates.liveness.mode);
  }, [gates, livenessDraft]);

  const updateMutation = useMutation({
    mutationFn: (patch: Partial<ProviderPolicy>) => apiClient.policy.update(patch),
  });
  const livenessMutation = useMutation({
    mutationFn: (mode: LivenessMode) => apiClient.authGates.update({ liveness: { mode } }),
  });

  if (isLoading || !data || !draft) {
    return (
      <div style={{ padding: 24, fontSize: 13, color: "var(--ad-fg-3)" }}>Loading…</div>
    );
  }

  const policyDirty =
    draft.factorCombination !== data.factorCombination ||
    draft.agentPolicy !== data.agentPolicy ||
    draft.agentRateLimitPerMin !== data.agentRateLimitPerMin;
  const livenessDirty =
    livenessDraft !== null && gates != null && livenessDraft !== gates.liveness.mode;
  const dirty = policyDirty || livenessDirty;
  const pending = publishing || updateMutation.isPending || livenessMutation.isPending;

  // Viewers get a read-only screen (#52): every edit funnels through `set`,
  // so a single guard freezes the draft; the publish footer is hidden below.
  // The server still enforces RBAC on PATCH.
  const set = <K extends keyof ProviderPolicy>(key: K, v: ProviderPolicy[K]) => {
    if (!canWrite) return;
    setDraft((d) => (d ? { ...d, [key]: v } : d));
  };

  const onDiscard = () => {
    setDraft(data);
    if (gates) setLivenessDraft(gates.liveness.mode);
    setError(null);
  };

  const onPublish = async () => {
    if (!dirty || pending) return;
    const livenessBlock =
      livenessDraft !== null
        ? livenessBlockReason(livenessDraft, gates?.availability, draft.factorCombination)
        : null;
    if (livenessDirty && livenessBlock) {
      setError(`Cannot publish liveness ${livenessDraft}: ${livenessBlock}`);
      return;
    }

    setPublishing(true);
    setError(null);
    try {
      const publishPolicy = async () => {
        const savedPolicy = await updateMutation.mutateAsync(draft);
        setDraft(savedPolicy);
        queryClient.setQueryData(["policy"], savedPolicy);
      };
      const publishLiveness = async () => {
        const savedGates = await livenessMutation.mutateAsync(livenessDraft!);
        setLivenessDraft(savedGates.liveness.mode);
        queryClient.setQueryData(["auth-gates"], savedGates);
      };

      // Each request must leave a valid server state if the following request
      // fails: publish the gate first when lowering it, otherwise policy first.
      const gateFirst =
        policyDirty &&
        livenessDirty &&
        gates != null &&
        LIVENESS_RANK[livenessDraft!] < LIVENESS_RANK[gates.liveness.mode];
      if (gateFirst) {
        await publishLiveness();
        await publishPolicy();
      } else {
        if (policyDirty) await publishPolicy();
        if (livenessDirty) await publishLiveness();
      }
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Couldn't save. Try again.",
      );
    } finally {
      setPublishing(false);
    }
  };

  return (
    <>
      <div className="ad-page-head">
        <div className="ad-page-head-l">
          <div className="ad-eyebrow">Set up</div>
          <h1 className="ad-h1">How people sign in</h1>
          <div className="ad-sub">
            Pick a sign-in method, then decide on liveness and AI agents. Changes apply to the
            next sign-in.
          </div>
        </div>
        <div className="ad-page-head-r">
          {/* Publish moved to the contextual bottom SaveBar. */}
          {!canWrite && <Pill kind="muted">Read-only</Pill>}
        </div>
      </div>

      <div className="ad-stack-lg">
        {/* Human policy */}
        <div className="ad-card">
          <div className="ad-card-head">
            <div>
              <div className="ad-card-title">Sign-in method</div>
              <div style={{ fontSize: 14, color: "var(--ad-fg-3)", marginTop: 4 }}>What people use to sign in to your app.</div>
            </div>
          </div>
          <div className="ad-grid-2">
            {POLICY_OPTIONS.map((opt) => (
              <PolicyCard
                key={opt.id}
                option={opt}
                active={draft.factorCombination === opt.id}
                onClick={() => set("factorCombination", opt.id)}
              />
            ))}
          </div>
          {FACTOR_REQUIRES_PHONE[draft.factorCombination] && (
            <div style={{ marginTop: 12 }}>
              <Banner
                icon={<Icons.Fingerprint />}
                title="Continues on their phone"
                sub="Motion needs a phone's sensors, so on a computer people scan a QR code and finish on their phone. Nothing to set up."
              />
            </div>
          )}
        </div>

        {/* Agent policy */}
        <div className="ad-card">
          <div className="ad-card-head">
            <div>
              <div className="ad-card-title">AI agents</div>
              <div style={{ fontSize: 14, color: "var(--ad-fg-3)", marginTop: 4 }}>AI agents that sign in on someone's behalf (sessions with <span className="ad-mono">isHuman: false</span>).</div>
            </div>
          </div>

          <div className="ad-stack-lg">
            <div className="ad-grid-2">
              <div className="ad-radio-card" data-checked={draft.agentPolicy === "allowed"} onClick={() => set("agentPolicy", "allowed")}>
                <div className="ad-radio-card-head">
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Icons.Robot size={16} style={{ color: "var(--ad-orange-ink)" } as React.CSSProperties} />
                    <div style={{ fontSize: 15, fontWeight: 600 }}>Allow agents</div>
                  </div>
                  <div className="ad-radio-card-dot" />
                </div>
                <div style={{ fontSize: 14, color: "var(--ad-fg-2)" }}>Agents sign in with a passkey only, within their own rate limit.</div>
              </div>
              <div className="ad-radio-card" data-checked={draft.agentPolicy === "blocked"} onClick={() => set("agentPolicy", "blocked")}>
                <div className="ad-radio-card-head">
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Icons.Lock size={16} style={{ color: "var(--ad-fg-2)" } as React.CSSProperties} />
                    <div style={{ fontSize: 15, fontWeight: 600 }}>Block agents</div>
                  </div>
                  <div className="ad-radio-card-dot" />
                </div>
                <div style={{ fontSize: 14, color: "var(--ad-fg-2)" }}>Refuse any sign-in marked as an agent. The strictest choice.</div>
              </div>
            </div>

            {draft.agentPolicy === "allowed" && (
              <div className="ad-field">
                <div className="ad-field-label">Agent rate limit</div>
                <div className="ad-row" style={{ gap: 14 }}>
                  <input
                    type="range"
                    min={AGENT_RATE_MIN}
                    max={AGENT_RATE_MAX}
                    step={5}
                    value={draft.agentRateLimitPerMin}
                    onChange={(e) => set("agentRateLimitPerMin", +e.target.value)}
                    style={{ flex: 1, accentColor: "var(--rb-orange)" } as React.CSSProperties}
                  />
                  <span className="ad-mono-or" style={{ minWidth: 80, textAlign: "right" }}>{draft.agentRateLimitPerMin} / min</span>
                </div>
                <div className="ad-field-hint">Sign-ins per minute for each agent. Separate from the limit for people.</div>
              </div>
            )}
          </div>
        </div>

        {/* Liveness auth gate — orthogonal to the policy above */}
        <LivenessGateCard
          mode={livenessDraft ?? gates?.liveness.mode ?? "off"}
          onSelect={(m) => canWrite && setLivenessDraft(m)}
          canWrite={canWrite}
          isLoading={gatesLoading || livenessDraft === null}
          pending={pending}
          availability={gates?.availability}
          factorCombination={draft.factorCombination}
          stats={analytics?.liveness24h}
        />

        {/* JSON preview */}
        <div className="ad-card">
          <div className="ad-card-head">
            <div className="ad-card-title">As JSON</div>
            <CopyBtn text={JSON.stringify(draft, null, 2)} small />
          </div>
          <div className="ad-code">
            <div className="ad-code-body">{`{
  `}<span className="tk-key">"factorCombination"</span>{`: `}<span className="tk-str">"{draft.factorCombination}"</span>{`,
  `}<span className="tk-key">"agentPolicy"</span>{`: `}<span className="tk-str">"{draft.agentPolicy}"</span>{`,
  `}<span className="tk-key">"agentRateLimitPerMin"</span>{`: `}<span className="tk-num">{draft.agentRateLimitPerMin}</span>{`
}`}</div>
          </div>
        </div>

        {error && (
          <div
            style={{
              padding: 12,
              borderRadius: 12,
              fontSize: 14,
              background: "color-mix(in oklab, var(--ad-error) 10%, transparent)",
              color: "var(--ad-error)",
            }}
          >
            {error}
          </div>
        )}

        <Banner
          icon={<Icons.Shield />}
          title="When changes apply"
          sub="Saved changes apply to the next sign-in. Someone already partway through may need to start again."
        />
      </div>

      <SaveBar
        visible={canWrite && dirty}
        pending={pending}
        onDiscard={onDiscard}
        onPublish={onPublish}
        label="Save"
        pendingLabel="Saving…"
      />
    </>
  );
}
